import {
  AEAD_AES_128_GCM,
  CipherSuite,
  KDF_HKDF_SHA256,
  KEM_DHKEM_X25519_HKDF_SHA256,
} from "hpke";
import {
  AeadId,
  KdfId,
  KemId,
  KeyConfig as OHTTPKeyConfig,
  OHTTPClient,
  type KeyConfig as ParsedOHTTPKeyConfig,
} from "ohttp-ts";

type OHTTPClientConfig = {
  status: string;
  key_config_url: string;
  gateway_url: string;
  relay_url?: string | null;
  request_content_type: string;
  response_content_type: string;
};

type ResolvedOHTTPClient = {
  client: OHTTPClient;
  relayURL: URL;
  requestContentType: string;
  responseContentType: string;
};

const OHTTP_CONFIG_TTL_MS = 5 * 60 * 1000;

let cachedClient:
  | {
      expiresAt: number;
      apiBaseURL: string;
      promise: Promise<ResolvedOHTTPClient>;
    }
  | undefined;

export async function fetchThroughOHTTP(
  apiBaseURL: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const base = normalizeAPIBaseURL(apiBaseURL);
  const resolved = await resolveOHTTPClient(base);
  const innerURL = new URL(path, base);
  const innerRequest = new Request(innerURL, {
    ...init,
    credentials: "omit",
  });
  const { init: outerInit, context } = await resolved.client.encapsulateRequest(innerRequest);
  const headers = new Headers(outerInit.headers);
  headers.set("Content-Type", resolved.requestContentType);
  headers.set("Accept", resolved.responseContentType);
  headers.set("Cache-Control", "no-store");

  const outerResponse = await fetch(resolved.relayURL, {
    ...outerInit,
    headers,
    cache: "no-store",
    credentials: "omit",
  });
  return context.decapsulateResponse(outerResponse);
}

function normalizeAPIBaseURL(rawValue: string) {
  const trimmed = rawValue.trim().replace(/\/+$/, "");
  return new URL(`${trimmed || "https://api.key.store"}/`);
}

async function resolveOHTTPClient(apiBaseURL: URL): Promise<ResolvedOHTTPClient> {
  const now = Date.now();
  const cacheKey = apiBaseURL.toString();
  if (cachedClient && cachedClient.apiBaseURL === cacheKey && cachedClient.expiresAt > now) {
    return cachedClient.promise;
  }

  const promise = buildOHTTPClient(apiBaseURL);
  cachedClient = {
    apiBaseURL: cacheKey,
    expiresAt: now + OHTTP_CONFIG_TTL_MS,
    promise,
  };
  return promise;
}

async function buildOHTTPClient(apiBaseURL: URL): Promise<ResolvedOHTTPClient> {
  const clientConfigURL = new URL("/ohttp/client-config", apiBaseURL);
  const configResponse = await fetch(clientConfigURL, {
    headers: { Accept: "application/json" },
    cache: "no-store",
    credentials: "omit",
  });
  if (!configResponse.ok) {
    throw new Error(`OHTTP client config failed with ${configResponse.status}`);
  }
  const config = (await configResponse.json()) as OHTTPClientConfig;
  if (config.status !== "ok") {
    throw new Error(`OHTTP client config is ${config.status}`);
  }
  if (!config.relay_url) {
    throw new Error("OHTTP analytics requires an advertised relay URL");
  }

  const keyConfigURL = new URL(config.key_config_url, apiBaseURL);
  const keyConfigResponse = await fetch(keyConfigURL, {
    headers: { Accept: "application/ohttp-keys" },
    cache: "no-store",
    credentials: "omit",
  });
  if (!keyConfigResponse.ok) {
    throw new Error(`OHTTP key config failed with ${keyConfigResponse.status}`);
  }

  const keyConfigs = OHTTPKeyConfig.parseMultiple(new Uint8Array(await keyConfigResponse.arrayBuffer()));
  const keyConfig = selectKeyConfig(keyConfigs);
  const suite = new CipherSuite(KEM_DHKEM_X25519_HKDF_SHA256, KDF_HKDF_SHA256, AEAD_AES_128_GCM);

  return {
    client: new OHTTPClient(suite, keyConfig),
    relayURL: new URL(config.relay_url, apiBaseURL),
    requestContentType: config.request_content_type || "message/ohttp-req",
    responseContentType: config.response_content_type || "message/ohttp-res",
  };
}

function selectKeyConfig(configs: ParsedOHTTPKeyConfig[]) {
  const selected = configs.find(
    (config) =>
      config.kemId === KemId.X25519_HKDF_SHA256 &&
      config.symmetricAlgorithms.some(
        (algorithm) => algorithm.kdfId === KdfId.HKDF_SHA256 && algorithm.aeadId === AeadId.AES_128_GCM,
      ),
  );
  if (!selected) {
    throw new Error("OHTTP key config does not advertise X25519/HKDF-SHA256/AES-128-GCM");
  }
  return selected;
}
